import { type Locale } from '@mfw/translations';
import { intervalToDuration } from 'date-fns';

import { isCivilDate, parseWireDay } from '@mfw/common';

import { formatWrittenDay } from './formatCivilDay';

export interface FormatOptions {
  dateOnly?: boolean;
  locale: Locale;
  /** Reads an INSTANT in this zone (e.g. the organization's) instead of the
   *  browser's. Ignored for a civil day, which has no zone to convert. */
  timeZone?: string;
}

const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

class FormatDate {
  static makeLocalizedDaysOfWeek(
    locale: Locale,
    variant: 'long' | 'short' = 'long',
  ): { value: (typeof DAYS)[number]; label: string }[] {
    const { format } = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: variant,
    });

    // Determine first day of week via Intl.Locale when available; default to Monday (1)
    let firstDayOfWeek = 1;
    try {
      const IntlWithLocale = Intl as unknown as {
        Locale?: new (loc: string) => { weekInfo?: { firstDay?: number } };
      };
      if (IntlWithLocale.Locale) {
        const loc = new IntlWithLocale.Locale(locale as unknown as string);
        const fd = loc.weekInfo?.firstDay;
        if (typeof fd === 'number') firstDayOfWeek = fd; // 0..6
      } else {
        // Fallback: US uses Sunday
        const localeStr = String(locale);
        if (/[-_]US\b/i.test(localeStr)) firstDayOfWeek = 0;
      }
    } catch {
      const localeStr = String(locale);
      if (/[-_]US\b/i.test(localeStr)) firstDayOfWeek = 0;
    }

    // Use a known Sunday as base in UTC (Aug 1, 2021 is Sunday)
    const baseSundayUtc = new Date(Date.UTC(2021, 7, 1));

    return [...Array(7).keys()].map((offset) => {
      const weekdayIndex = (firstDayOfWeek + offset) % 7;
      const date = new Date(baseSundayUtc);
      date.setUTCDate(baseSundayUtc.getUTCDate() + weekdayIndex);
      return {
        label: format(date),
        value: DAYS[weekdayIndex],
      };
    });
  }

  static getFirstDayOfWeek(locale: Locale) {
    // Determine first day of week via Intl.Locale when available; default to Monday (1)
    let firstDayOfWeek = 1;
    try {
      const IntlWithLocale = Intl as unknown as {
        Locale?: new (loc: string) => { weekInfo?: { firstDay?: number } };
      };
      if (IntlWithLocale.Locale) {
        const loc = new IntlWithLocale.Locale(locale as unknown as string);
        const fd = loc.weekInfo?.firstDay;
        if (typeof fd === 'number') firstDayOfWeek = fd; // 0..6
      } else {
        const localeStr = String(locale);
        if (/[-_]US\b/i.test(localeStr)) firstDayOfWeek = 0;
      }
    } catch {
      const localeStr = String(locale);
      if (/[-_]US\b/i.test(localeStr)) firstDayOfWeek = 0;
    }

    const { format } = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: 'long',
    });
    const baseSundayUtc = new Date(Date.UTC(2021, 7, 1));
    const date = new Date(baseSundayUtc);
    date.setUTCDate(baseSundayUtc.getUTCDate() + firstDayOfWeek);
    return format(date).toLocaleLowerCase() as (typeof DAYS)[number];
  }

  /**
   *
   * @param seconds Seconds to convert to duration.
   * @param format Long - shows hours, minutes and seconds (hours hidden when zero), Medium - shows minutes and seconds.
   * @param */
  static getDurationFromSecondsByFormat(
    seconds: number,
    format: 'long' | 'medium',
  ) {
    const duration = intervalToDuration({ end: seconds * 1000, start: 0 });
    const minutesAndSeconds = `${
      duration.minutes ? duration.minutes.toString().padStart(2, '0') : '00'
    }:${duration.seconds ? duration.seconds.toString().padStart(2, '0') : '00'}`;

    if (format === 'long' && duration.hours) {
      return `${duration.hours.toString().padStart(2, '0')}:${minutesAndSeconds}`;
    }
    return minutesAndSeconds;
  }

  static getDateFromWeekDay(
    date: Date,
    day: ReturnType<
      (typeof FormatDate)['makeLocalizedDaysOfWeek']
    >[number]['value'],
  ) {
    const dayIndex = DAYS.indexOf(day);
    const currentDayIndex = date.getDay();
    const diff = (dayIndex - currentDayIndex + 7) % 7;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
  }

  static format(date: string, options: FormatOptions): string {
    // Civil days ('yyyy-MM-dd') must not go through `new Date` — that reads
    // them as UTC midnight and renders the previous day west of UTC. Nor do
    // they get a time of day: they have none, so it would always read 12:00 AM.
    const isDay = isCivilDate(date);
    const parsed = parseWireDay(date);
    if (options.dateOnly || isDay) {
      // Instants read in the requested zone, so the DAY is taken there too.
      const day =
        options.timeZone && !isDay
          ? parseWireDay(
              new Intl.DateTimeFormat('en-CA', {
                timeZone: options.timeZone,
              }).format(parsed),
            )
          : parsed;
      return formatWrittenDay(day, options.locale);
    }
    const time = parsed.toLocaleTimeString(options.locale, {
      hour: '2-digit',
      minute: '2-digit',
      ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    });
    const day = options.timeZone
      ? parseWireDay(
          new Intl.DateTimeFormat('en-CA', {
            timeZone: options.timeZone,
          }).format(parsed),
        )
      : parsed;
    return `${formatWrittenDay(day, options.locale)}, ${time}`;
  }

  // TODO Extend with actual date object, if date matters in the future
  static fromSecondsToDateTime(numberOfSeconds: number) {
    // Callers format the result with date-fns, which THROWS on an Invalid Date
    // — so a NaN coming in must not become one going out.
    const total = Number.isFinite(numberOfSeconds) ? numberOfSeconds : 0;
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return new Date(2026, 1, 1, hours, minutes, seconds);
  }

  static formatSecondsToMinutesAndSeconds(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  static formatDateTimeToSeconds(date?: Date) {
    // An Invalid Date is TRUTHY, and every getter on it answers NaN. A MUI
    // time field hands one over for a half-typed entry, so `!date` alone let
    // NaN into every duration sum built from these (Sentry MYFITWORLD-V2-6S).
    if (!date || Number.isNaN(date.getTime())) return 0;
    return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
  }

  static currentTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /** 24h `HH:MM`, e.g. for a message timestamp. */
  static formatTime(date: Date, locale: Locale): string {
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static isSameCivilDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  static isYesterday(date: Date, now: Date = new Date()): boolean {
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return FormatDate.isSameCivilDay(date, yesterday);
  }
}

export default FormatDate;
